import {
  ArrowDown,
  ArrowUp,
  Code,
  Copy,
  FileText,
  Heading,
  Image as ImageIcon,
  Link2,
  Loader2,
  Minus,
  Monitor,
  MousePointerClick,
  Plus,
  Smartphone,
  Trash2,
  Type,
  Upload,
  Video,
} from "lucide-react";
import { useNotify } from "ra-core";
import { useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  BLOCK_TYPES,
  STYLES,
  newBlock,
  renderEmail,
  uploadEmailAsset,
  youtubeThumbnail,
  type EmailBlock,
  type EmailBlockType,
  type EmailContent,
} from "./email";

const BLOCK_ICON: Record<EmailBlockType, typeof Type> = {
  heading: Heading,
  text: Type,
  image: ImageIcon,
  button: MousePointerClick,
  video: Video,
  file: FileText,
  html: Code,
  divider: Minus,
};

/** Button that uploads a file and hands back its public URL. */
const UploadButton = ({
  accept,
  label,
  onUploaded,
}: {
  accept: string;
  label: string;
  onUploaded: (url: string, file: File) => void;
}) => {
  const notify = useNotify();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={accept}
        className="hidden"
        onChange={async (event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (!file) return;
          setBusy(true);
          try {
            onUploaded(await uploadEmailAsset(file), file);
          } catch (error) {
            notify(error instanceof Error ? error.message : "Error al subir", {
              type: "error",
            });
          } finally {
            setBusy(false);
          }
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={() => input.current?.click()}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Upload className="h-4 w-4" />
        )}
        {busy ? "Subiendo…" : label}
      </Button>
    </>
  );
};

const Small = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs text-muted-foreground">{children}</p>
);

/** The fields of one block. */
const BlockFields = ({
  block,
  onChange,
}: {
  block: EmailBlock;
  onChange: (block: EmailBlock) => void;
}) => {
  const set = (patch: Record<string, unknown>) =>
    onChange({ ...block, ...patch } as EmailBlock);

  switch (block.type) {
    case "heading":
      return (
        <Input
          value={block.text}
          placeholder="Ej.: {nombre}, última semana de oferta"
          onChange={(event) => set({ text: event.target.value })}
        />
      );
    case "text":
      return (
        <div className="flex flex-col gap-1">
          <Textarea
            rows={5}
            value={block.text}
            placeholder="Escribe el mensaje. Deja una línea en blanco para separar párrafos."
            onChange={(event) => set({ text: event.target.value })}
          />
          <Small>
            {
              "**negrita** · *cursiva* · [texto del enlace](https://…) · {nombre} pone el nombre de cada persona"
            }
          </Small>
        </div>
      );
    case "image":
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <UploadButton
              accept="image/*"
              label={block.url ? "Cambiar imagen" : "Subir imagen"}
              onUploaded={(url, file) =>
                set({ url, alt: block.alt || file.name.replace(/\.\w+$/, "") })
              }
            />
            {block.url ? (
              <img
                src={block.url}
                alt=""
                className="h-12 rounded border object-cover"
              />
            ) : null}
          </div>
          <Input
            value={block.url}
            placeholder="…o pega la dirección de una imagen (https://…)"
            onChange={(event) => set({ url: event.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={block.alt ?? ""}
              placeholder="Descripción (si no carga)"
              onChange={(event) => set({ alt: event.target.value })}
            />
            <Input
              value={block.link ?? ""}
              placeholder="Al pulsar, abrir… (opcional)"
              onChange={(event) => set({ link: event.target.value })}
            />
          </div>
          <Small>
            Para un carrusel, pon varias imágenes seguidas. Ancho ideal: 1200
            px.
          </Small>
        </div>
      );
    case "button":
      return (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
          <Input
            value={block.label}
            placeholder="Texto: Quiero mi equipo"
            onChange={(event) => set({ label: event.target.value })}
          />
          <Input
            value={block.url}
            placeholder="https://guladeagua.es/…  ·  tel:614404301"
            onChange={(event) => set({ url: event.target.value })}
          />
          <div className="flex overflow-hidden rounded-md border">
            {(["dark", "lime"] as const).map((variant) => (
              <button
                key={variant}
                type="button"
                onClick={() => set({ variant })}
                className={cn(
                  "px-3 text-xs font-medium",
                  (block.variant ?? "dark") === variant
                    ? "bg-foreground text-background"
                    : "hover:bg-muted",
                )}
              >
                {variant === "dark" ? "Negro" : "Lima"}
              </button>
            ))}
          </div>
        </div>
      );
    case "video": {
      const auto = youtubeThumbnail(block.url ?? "");
      return (
        <div className="flex flex-col gap-2">
          <Input
            value={block.url}
            placeholder="Enlace al vídeo (YouTube, Instagram, TikTok, Drive…)"
            onChange={(event) => set({ url: event.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={block.label ?? ""}
              placeholder="Texto del botón: Ver el vídeo"
              onChange={(event) => set({ label: event.target.value })}
            />
            <div className="flex items-center gap-2">
              <UploadButton
                accept="image/*"
                label={block.thumbnail ? "Cambiar portada" : "Subir portada"}
                onUploaded={(url) => set({ thumbnail: url })}
              />
              {block.thumbnail ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:underline"
                  onClick={() => set({ thumbnail: "" })}
                >
                  Quitar
                </button>
              ) : null}
            </div>
          </div>
          <Small>
            Los correos no reproducen vídeo: se ve la portada con un botón que
            lo abre.{" "}
            {auto && !block.thumbnail
              ? "De YouTube cogemos la portada sola."
              : "Sube una captura del vídeo como portada."}
          </Small>
        </div>
      );
    }
    case "file":
      return (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <UploadButton
              accept=".pdf,application/pdf,image/*"
              label={block.url ? "Cambiar archivo" : "Subir PDF"}
              onUploaded={(url, file) =>
                set({
                  url,
                  name: block.name || file.name.replace(/\.\w+$/, ""),
                })
              }
            />
            {block.url ? (
              <a
                href={block.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs hover:underline"
              >
                <Link2 className="h-3 w-3" />
                Ver archivo
              </a>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={block.name ?? ""}
              placeholder="Nombre: catálogo Gula"
              onChange={(event) => set({ name: event.target.value })}
            />
            <Input
              value={block.label ?? ""}
              placeholder="Texto del botón (opcional)"
              onChange={(event) => set({ label: event.target.value })}
            />
          </div>
          <Small>
            Va como botón de descarga, no como adjunto: así el correo no cae en
            spam.
          </Small>
        </div>
      );
    case "html":
      return (
        <div className="flex flex-col gap-1">
          <Textarea
            rows={8}
            value={block.html}
            className="font-mono text-xs"
            placeholder="<table>…</table>  — pega aquí el HTML del diseño que te ha hecho Claude"
            onChange={(event) => set({ html: event.target.value })}
          />
          <Small>
            {
              "Pide a Claude «un correo en HTML con estilos en línea, 600 px de ancho, sin <head>». Los enlaces se cuentan solos y {nombre} también funciona."
            }
          </Small>
        </div>
      );
    case "divider":
      return <Small>Una línea para separar secciones.</Small>;
  }
};

/** Subject, style and blocks on the left; the email as it will arrive on the right. */
export const EmailContentEditor = ({
  value,
  onChange,
  previewName = "María",
  readOnly,
}: {
  value: EmailContent;
  onChange: (value: EmailContent) => void;
  previewName?: string;
  readOnly?: boolean;
}) => {
  const [device, setDevice] = useState<"desktop" | "mobile">("desktop");
  const [adding, setAdding] = useState<number | null>(null);
  const preview = useMemo(
    () => renderEmail(value, { firstName: previewName }),
    [value, previewName],
  );

  const setBlocks = (blocks: EmailBlock[]) => onChange({ ...value, blocks });
  const update = (index: number, block: EmailBlock) =>
    setBlocks(value.blocks.map((item, i) => (i === index ? block : item)));
  const move = (index: number, delta: number) => {
    const blocks = [...value.blocks];
    const [item] = blocks.splice(index, 1);
    blocks.splice(index + delta, 0, item);
    setBlocks(blocks);
  };
  const insert = (index: number, type: EmailBlockType) => {
    const blocks = [...value.blocks];
    blocks.splice(index, 0, newBlock(type));
    setBlocks(blocks);
    setAdding(null);
  };

  const AddMenu = ({ index }: { index: number }) =>
    adding === index ? (
      <div className="grid grid-cols-2 gap-1.5 rounded-xl border border-dashed p-2 sm:grid-cols-4">
        {BLOCK_TYPES.map((item) => {
          const Icon = BLOCK_ICON[item.type];
          return (
            <button
              key={item.type}
              type="button"
              onClick={() => insert(index, item.type)}
              className="flex flex-col items-start gap-0.5 rounded-lg px-2.5 py-2 text-left hover:bg-muted"
            >
              <span className="flex items-center gap-1.5 text-sm font-medium">
                <Icon className="h-4 w-4" />
                {item.label}
              </span>
              <span className="text-[11px] leading-tight text-muted-foreground">
                {item.hint}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setAdding(null)}
          className="col-span-full text-xs text-muted-foreground hover:underline"
        >
          Cancelar
        </button>
      </div>
    ) : (
      <button
        type="button"
        onClick={() => setAdding(index)}
        className="group flex h-6 items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <span className="h-px flex-1 bg-border group-hover:bg-foreground/30" />
        <Plus className="h-3.5 w-3.5" />
        Añadir bloque
        <span className="h-px flex-1 bg-border group-hover:bg-foreground/30" />
      </button>
    );

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <fieldset disabled={readOnly} className="flex min-w-0 flex-col gap-4">
        <div className="grid grid-cols-1 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Asunto</Label>
            <Input
              value={value.subject}
              placeholder="{nombre}, tu agua sin cal ni cloro por 499 €"
              onChange={(event) =>
                onChange({ ...value, subject: event.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Texto de vista previa</Label>
            <Input
              value={value.preheader ?? ""}
              placeholder="Lo que se lee junto al asunto en la bandeja (opcional)"
              onChange={(event) =>
                onChange({ ...value, preheader: event.target.value })
              }
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Estilo</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {STYLES.map((style) => (
                <button
                  key={style.value}
                  type="button"
                  onClick={() => onChange({ ...value, style: style.value })}
                  className={cn(
                    "flex flex-col gap-0.5 rounded-xl border p-2.5 text-left transition-colors",
                    value.style === style.value
                      ? "border-foreground bg-muted"
                      : "hover:bg-muted/60",
                  )}
                >
                  <span className="text-sm font-medium">{style.label}</span>
                  <span className="text-[11px] leading-snug text-muted-foreground">
                    {style.hint}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label>Contenido</Label>
          {!readOnly ? <AddMenu index={0} /> : null}
          {value.blocks.map((block, index) => {
            const Icon = BLOCK_ICON[block.type];
            const meta = BLOCK_TYPES.find((item) => item.type === block.type);
            return (
              <div key={block.id ?? index} className="flex flex-col gap-1.5">
                <div className="flex flex-col gap-2 rounded-xl border bg-card p-3">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      <Icon className="h-3.5 w-3.5" />
                      {meta?.label}
                    </span>
                    {!readOnly ? (
                      <div className="ml-auto flex items-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                          title="Subir"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={index === value.blocks.length - 1}
                          onClick={() => move(index, 1)}
                          title="Bajar"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            const blocks = [...value.blocks];
                            blocks.splice(index + 1, 0, {
                              ...block,
                              id: newBlock(block.type).id,
                            });
                            setBlocks(blocks);
                          }}
                          title="Duplicar"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() =>
                            setBlocks(
                              value.blocks.filter((_, i) => i !== index),
                            )
                          }
                          title="Quitar"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  <BlockFields
                    block={block}
                    onChange={(next) => update(index, next)}
                  />
                </div>
                {!readOnly ? <AddMenu index={index + 1} /> : null}
              </div>
            );
          })}
        </div>
      </fieldset>

      <div className="flex min-w-0 flex-col gap-2 xl:sticky xl:top-4 xl:self-start">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">
            Así llega{" "}
            <span className="font-normal text-muted-foreground">
              (a {previewName})
            </span>
          </span>
          <div className="flex overflow-hidden rounded-md border">
            {(
              [
                ["desktop", Monitor, "Ordenador"],
                ["mobile", Smartphone, "Móvil"],
              ] as const
            ).map(([key, Icon, label]) => (
              <button
                key={key}
                type="button"
                title={label}
                onClick={() => setDevice(key)}
                className={cn(
                  "px-2.5 py-1.5",
                  device === key
                    ? "bg-foreground text-background"
                    : "hover:bg-muted",
                )}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-xl border bg-muted/40 p-3 text-sm">
          <p className="truncate font-semibold">
            {preview.subject || "(sin asunto)"}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            Gula de Agua &lt;notificaciones@guladeagua.es&gt;
            {value.preheader ? ` · ${value.preheader}` : ""}
          </p>
        </div>
        <div className="flex justify-center overflow-hidden rounded-xl border bg-[#f4f4f0]">
          <iframe
            title="Vista previa del correo"
            srcDoc={preview.html}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            className={cn(
              "h-[70vh] min-h-[520px] bg-white transition-all",
              device === "mobile" ? "w-[380px]" : "w-full",
            )}
          />
        </div>
      </div>
    </div>
  );
};
